'use client'
import React, { useState, useEffect } from 'react'
import { socket } from '../Containers/MainContainer'
import Skeleton from 'react-loading-skeleton'
import { Link } from 'react-router-dom'
import { ArrowUp, ArrowDown, Layers, Users, DollarSign, TrendingUp, AlertCircle, Award, ChevronRight, Activity, Users2, Gamepad, Target, Calendar, TrendingDown } from 'lucide-react'
import { numberWithCommas } from '../utils'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts'

function PlutoMain() {
  // Dashboard state
  const [dashboardData, setDashboardData] = useState({
    activeUsers: undefined,
    activeUsersMonthly: undefined,
    revenue: undefined,
    spendings: undefined,
    profits: undefined,
    monthlyRev: undefined,
    monthlyTarget: undefined,
    monthlyPossibility: undefined,
    top10Offers: undefined,
    last10Offers: undefined,
    last10Users: undefined,
    rank: undefined,
    revt: undefined,
    withT: undefined,
    uid: -1,
    monthlyRevThis: undefined,
    thisMonthTarget:undefined,
    dataWalls: undefined,
    dataWallsX: undefined,
    loading: true
  })

  const getDateLabel = (type, daysBack) => {
    const today = new Date();
    
    if (type === 'day') {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() - daysBack);
      return targetDate.toLocaleDateString('en-US', { weekday: 'long' });
    }
    
    if (type === 'month') {
      const targetDate = new Date(today);
      targetDate.setMonth(today.getMonth() - daysBack);
      return targetDate.toLocaleDateString('en-US', { month: 'long' });
    }
    
    return `${type} ${daysBack}`;
  }

  // Calculate monthly progress and needed recovery
  const getMonthlyProgress = () => {
    const MONTHLY_TARGET = dashboardData.thisMonthTarget; // $85k monthly target
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const daysPassed = today.getDate();
    const daysRemaining = daysInMonth - daysPassed;
    
    const currentRevenue = monthlyRevThis || 0;
    const targetByNow = (MONTHLY_TARGET / daysInMonth) * daysPassed;
    const shortfall = Math.max(0, targetByNow - currentRevenue);
    const dailyNeededToRecover = daysRemaining > 0 ? ((MONTHLY_TARGET - currentRevenue) / daysRemaining) : 0;
    const progressPercentage = (currentRevenue / MONTHLY_TARGET) * 100;
    
    return {
      MONTHLY_TARGET,
      currentRevenue,
      targetByNow,
      shortfall,
      dailyNeededToRecover,
      progressPercentage,
      daysPassed,
      daysRemaining,
      daysInMonth,
      isOnTrack: currentRevenue >= targetByNow
    };
  };

  // Data fetching from socket
  useEffect(() => {
    socket.emit('getHomeAdmin')
    
    socket.on('response_get_home_admin_data', (data) => {
      let cc = data.dataWallsX;

      for (let i = 0; i < data.dataWalls.length; i++) {
        const currentWall = data.dataWalls[i];
        const match = cc.find(w => w.wall_name === currentWall.wall_name);
      
        if (!match) {
          cc.push({ ...currentWall, usd2: 0 });
        } else {
          const prevUsd = match.usd;
          match.usd = currentWall.usd;
          match.usd2 = prevUsd;
        }
      }
      console.log(data)
      setDashboardData(prev => ({
        ...prev,
        top10Offers: data.top10OffersMadeToday,
        thisMonthTarget: parseFloat(data.monthlyTarget[data.monthlyTarget.length-1]),
        monthlyRevThis: parseFloat(data.monthlyRev[data.monthlyRev.length-1]),
        last10Offers: data.last10offers,
        last10Users: data.last10Users,
        monthlyRev: data.monthlyRev,
        monthlyTarget: data.monthlyTarget,
        monthlyPossibility: data.monthlyPosibility,
        rank: data.rank,
        revt: data.dailyR,
        dataWallsX: cc,
        dataWalls: data.dataWalls,
        withT: data.dailyW,
        uid: data.uid,
      }))
    })
    
    socket.on('response_get_home_admin_data_activeusers', (data) => {
      setDashboardData(prev => ({
        ...prev,
        activeUsers: data.activeUsers
      }))
    })
    
    socket.on('response_get_home_admin_data_activeusersmonthly', (data) => {
      setDashboardData(prev => ({
        ...prev,
        activeUsersMonthly: data.activeUsersMonthly
      }))
    })
    
    socket.on('response_get_home_admin_data_dailyrev', (data) => {
      setDashboardData(prev => ({
        ...prev,
        revenue: data.dataRev,
        profits: data.profits,
        spendings: data.dataWith,
        loading: false
      }))
    })

    // Cleanup
    return () => {
      socket.off('response_get_home_admin_data')
      socket.off('response_get_home_admin_data_activeusers')
      socket.off('response_get_home_admin_data_activeusersmonthly')
      socket.off('response_get_home_admin_data_dailyrev')
    }
  }, [])

  // Format number to K, M format
  const formatNumber = (number) => {
    if (number >= 1000000) return (number / 1000000).toFixed(1) + 'M'
    if (number >= 1000) return (number / 1000).toFixed(1) + 'K'
    return number
  }

  // Stat Card component
  const StatCard = ({ title, value, icon: Icon, change, color = "blue", isLoading, subtitle }) => (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
      {isLoading ? (
        <Skeleton height={100} />
      ) : (
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-${color}-500/20`}>
            <Icon className={`h-5 w-5 text-${color}-400`} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-400">{title}</div>
            <div className="text-xl font-bold text-gray-200">{value}</div>
            {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
            {change !== undefined && (
              <div className={`flex items-center text-sm mt-1 ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change >= 0 ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
                <span>{Math.abs(change).toFixed(1)}% {change >= 0 ? 'increase' : 'decrease'}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )

  // Monthly Target Card - Prominent display
  const MonthlyTargetCard = () => {
    const progress = getMonthlyProgress();
    console.log(progress)
    return (
      <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-lg p-6 border border-blue-700/50 col-span-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Target className="h-8 w-8 text-blue-400" />
            <div>
              <h3 className="text-xl font-bold text-gray-200">Monthly Target Progress</h3>
              <p className="text-sm text-gray-400">${numberWithCommas(dashboardData.thisMonthTarget)} target for {new Date().toLocaleDateString('en-US', { month: 'long' })}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-400">{progress.progressPercentage.toFixed(1)}%</div>
            <div className="text-sm text-gray-400">Complete</div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-700 rounded-full h-3 mb-4">
          <div 
            className={`h-3 rounded-full transition-all duration-300 ${
              progress.isOnTrack ? 'bg-green-500' : 'bg-red-500'
            }`}
            style={{ width: `${Math.min(100, progress.progressPercentage)}%` }}
          ></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-400">Current Revenue</div>
            <div className="text-lg font-bold text-green-400">${numberWithCommas(progress.currentRevenue.toFixed(0))}</div>
          </div>
          <div>
            <div className="text-gray-400">Should Have Made</div>
            <div className="text-lg font-bold text-blue-400">${numberWithCommas(progress.targetByNow.toFixed(0))}</div>
          </div>
          <div>
            <div className="text-gray-400">{progress.shortfall > 0 ? 'Behind By' : 'Ahead By'}</div>
            <div className={`text-lg font-bold ${progress.shortfall > 0 ? 'text-red-400' : 'text-green-400'}`}>
              ${numberWithCommas(Math.abs(progress.currentRevenue - progress.targetByNow).toFixed(0))}
            </div>
          </div>
          <div>
            <div className="text-gray-400">Daily Needed ({progress.daysRemaining}d left)</div>
            <div className="text-lg font-bold text-orange-400">${numberWithCommas(progress.dailyNeededToRecover.toFixed(0))}</div>
          </div>
        </div>

        {!progress.isOnTrack && progress.shortfall > 0 && (
          <div className="mt-4 p-3 bg-red-900/20 border border-red-800/30 rounded-lg">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">Recovery Required: Need ${numberWithCommas(progress.dailyNeededToRecover.toFixed(0))}/day to hit target</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getGradient = (color1, color2, id) => {
    return (
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor={color1} stopOpacity={0.8}/>
        <stop offset="95%" stopColor={color2} stopOpacity={0.2}/>
      </linearGradient>
    )
  }

  const UsersChart = ({ data }) => {
    if (!data) return <Skeleton height={300} />
    
    const chartData = data.map((val, idx) => ({
      date: getDateLabel('day', 6 - idx),
      users: val
    }))
    
    return (
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2" />
          Active Users (Last 7 Days)
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              {getGradient('#4158D0', '#4158D0', 'colorUsers')}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
            <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} />
            <YAxis stroke="#9CA3AF" fontSize={12} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
              formatter={(value) => [formatNumber(value), 'Users']}
            />
            <Area 
              type="monotone" 
              dataKey="users" 
              stroke="#4F46E5" 
              fillOpacity={1} 
              fill="url(#colorUsers)" 
              name="Users"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )
  }

  // Revenue Table component
  const RevenueTable = ({ data, spendings, profits }) => {
    if (!data || !spendings || !profits) return <Skeleton height={300} />
    
    const tableData = data.map((val, idx) => ({
      day: idx + 1,
      revenue: val,
      spendings: spendings[idx] || 0,
      profits: profits[idx] || 0,
      profitMargin: val > 0 ? ((profits[idx] || 0) / val * 100) : 0,
      date: getDateLabel('day', data.length - 1 - idx)
    })).slice(-10).reverse() // Show last 10 days, most recent first
    
    return (
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
          <DollarSign className="h-5 w-5 mr-2" />
          Revenue vs Withdrawals (Last 10 Days)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-400">Date</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Revenue</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Withdrawals</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Profits</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                  <td className="py-3 px-4 font-medium text-gray-300">
                    {row.date} {index === 0 ? '(Today)' : ''}
                  </td>
                  <td className="text-right py-3 px-4 font-mono text-green-400">${numberWithCommas(parseFloat(row.revenue).toFixed(2))}</td>
                  <td className="text-right py-3 px-4 font-mono text-red-400">${numberWithCommas(parseFloat(row.spendings).toFixed(2))}</td>
                  <td className="text-right py-3 px-4 font-mono text-blue-400">${numberWithCommas(parseFloat(row.profits).toFixed(2))}</td>
                  <td className="text-right py-3 px-4 font-mono text-gray-300">{row.profitMargin.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // Walls Table component
  const WallsTable = ({ dataWallsX }) => {
    if (!dataWallsX) return <Skeleton height={300} />
    
    const tableData = dataWallsX.map(x => ({
      name: x.wall_name,
      monthlyEarnings: x.usd,
      monthlyTarget: 5000,
      todayEarnings: x.usd2 || 0,
      todayTarget: 167,
      monthlyProgress: (x.usd / 5000 * 100),
      todayProgress: ((x.usd2 || 0) / 167 * 100)
    })).reverse()
    
    return (
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
          <Activity className="h-5 w-5 mr-2" />
          Wall Earnings & Targets
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-400">Wall Name</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Today's Earnings</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Today %</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Monthly Earnings</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Monthly %</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((wall, index) => (
                <tr key={index} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                  <td className="py-3 px-4 font-medium text-gray-300">{wall.name}</td>
                  <td className="text-right py-3 px-4 font-mono text-green-400">${numberWithCommas(wall.todayEarnings.toFixed(2))}</td>
                  <td className={`text-right py-3 px-4 font-mono ${
                    wall.todayProgress >= 100 ? 'text-green-400' : 
                    wall.todayProgress >= 60 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {wall.todayProgress.toFixed(1)}%
                  </td>
                  <td className="text-right py-3 px-4 font-mono text-blue-400">${numberWithCommas(wall.monthlyEarnings.toFixed(2))}</td>
                  <td className={`text-right py-3 px-4 font-mono ${
                    wall.monthlyProgress >= 100 ? 'text-green-400' : 
                    wall.monthlyProgress >= 60 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {wall.monthlyProgress.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // Monthly Revenue Table
  const MonthlyRevenueTable = ({ monthlyRev, monthlyTarget }) => {
    if (!monthlyRev || !monthlyTarget) return <Skeleton height={300} />
    
    const tableData = monthlyRev.map((val, idx) => ({
      month: idx + 1,
      revenue: val,
      target: dashboardData.thisMonthTarget || 85000,
      achievement: ((val / (dashboardData.thisMonthTarget || 85000)) * 100),
      difference: val - (dashboardData.thisMonthTarget || 85000)
    })).slice(-12).reverse() // Show last 12 months, most recent first
    
    return (
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2" />
          Monthly Revenue & Target (Last 12 Months)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-400">Month</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Revenue</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Target</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Achievement %</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Difference</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                  <td className="py-3 px-4 font-medium text-gray-300">
                    {getDateLabel('month', index)} {index === 0 ? '(Current)' : ''}
                  </td>
                  <td className="text-right py-3 px-4 font-mono text-green-400">${numberWithCommas(parseFloat(row.revenue).toFixed(0))}</td>
                  <td className="text-right py-3 px-4 font-mono text-gray-400">${numberWithCommas(parseFloat(row.target).toFixed(0))}</td>
                  <td className={`text-right py-3 px-4 font-mono ${
                    row.achievement >= 100 ? 'text-green-400' : 
                    row.achievement >= 80 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {row.achievement.toFixed(1)}%
                  </td>
                  <td className={`text-right py-3 px-4 font-mono ${row.difference >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {row.difference >= 0 ? '+' : ''}${numberWithCommas(row.difference.toFixed(0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const { 
    activeUsers,
    revenue,
    spendings,
    profits,
    monthlyRev,
    monthlyTarget,
    top10Offers,
    last10Offers,
    last10Users,
    revt,
    withT,
    monthlyRevThis,
    dataWallsX,
    loading
  } = dashboardData

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-200">Analytics Dashboard</h1>
          <p className="mt-2 text-gray-400">Revenue overview and performance metrics</p>
          <div className="mt-2 text-sm text-gray-500">
            Today: {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>

        {/* Monthly Target - Prominent Section */}
        <div className="grid grid-cols-1 gap-4 mb-6">
          <MonthlyTargetCard />
        </div>

        {/* Key metrics cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard 
            title="Daily Revenue" 
            value={`$${numberWithCommas(revt || 0)}`} 
            icon={DollarSign} 
            change={revenue && revenue.length > 1 ? ((revt/(revenue[revenue.length-2] || 1))-1)*100 : 0}
            color="green"
            isLoading={!revenue} 
          />
          <StatCard 
            title="Daily Withdrawals" 
            value={`$${numberWithCommas(withT || 0)}`} 
            icon={TrendingDown} 
            change={spendings && spendings.length > 1 ? ((withT/(spendings[spendings.length-2] || 1))-1)*100 : 0}
            color="red"
            isLoading={!spendings} 
          />
          <StatCard 
            title="Monthly Revenue" 
            value={`$${numberWithCommas(monthlyRevThis || 0)}`} 
            icon={Layers} 
            change={monthlyRev && monthlyRev.length > 1 ? ((monthlyRev[monthlyRev.length-1]/(monthlyRev[monthlyRev.length-2] || 1))-1)*100 : 0}
            color="blue"
            isLoading={!monthlyRev}
            subtitle={`Target: ${numberWithCommas(dashboardData.thisMonthTarget)}`}
          />
          <StatCard 
            title="Active Users Today" 
            value={activeUsers ? numberWithCommas(activeUsers[activeUsers.length - 1] || 0) : '0'}
            icon={Users} 
            change={activeUsers && activeUsers.length > 1 ? ((activeUsers[activeUsers.length - 1]/(activeUsers[activeUsers.length - 2] || 1))-1)*100 : 0}
            color="purple"
            isLoading={!activeUsers} 
          />
        </div>

        {/* Charts and Tables */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <RevenueTable data={revenue} spendings={spendings} profits={profits} />
          <UsersChart data={activeUsers} />
          <MonthlyRevenueTable monthlyRev={monthlyRev} monthlyTarget={monthlyTarget} />
          <WallsTable dataWallsX={dataWallsX} />
        </div>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
              <Gamepad className="h-5 w-5 mr-2" />
              Top 10 Offers Made Today
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-400">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-400">Wall</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-400">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {!top10Offers ? (
                    Array(10).fill().map((_, i) => (
                      <tr key={i}>
                        <td className="py-3 px-4"><Skeleton height={20} /></td>
                        <td className="py-3 px-4"><Skeleton height={20} /></td>
                        <td className="py-3 px-4"><Skeleton height={20} /></td>
                      </tr>
                    ))
                  ) : (
                    top10Offers.map((user, index) => (
                      <tr key={index} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                        <td className="py-3 px-4 text-gray-300">{user.name}</td>
                        <td className="py-3 px-4 text-gray-300">{user.wall_name}</td>
                        <td className="text-right py-3 px-4 text-gray-300">{user.c}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
              <Gamepad className="h-5 w-5 mr-2" />
              Last 10 Offers Made Today
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-400">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-400">Wall</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-400">Country</th>
                  </tr>
                </thead>
                <tbody>
                  {!last10Offers ? (
                    Array(10).fill().map((_, i) => (
                      <tr key={i}>
                        <td className="py-3 px-4"><Skeleton height={20} /></td>
                        <td className="py-3 px-4"><Skeleton height={20} /></td>
                        <td className="py-3 px-4"><Skeleton height={20} /></td>
                      </tr>
                    ))
                  ) : (
                    last10Offers.map((user, index) => (
                      <tr key={index} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                        <td className="py-3 px-4 text-gray-300">{user.name}</td>
                        <td className="py-3 px-4 text-gray-300">{user.wall}</td>
                        <td className="text-right py-3 px-4 text-gray-300">{user.country}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* User Tables */}
       

        {/* Last 10 Users Table */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
            <Users2 className="h-5 w-5 mr-2" />
            Last 10 Accounts Created
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-400">ID</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-400">Balance</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-400">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-400">Email Provider</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-400">Country</th>
                </tr>
              </thead>
              <tbody>
                {!last10Users ? (
                  Array(10).fill().map((_, i) => (
                    <tr key={i}>
                      <td className="py-3 px-4"><Skeleton height={20} /></td>
                      <td className="py-3 px-4"><Skeleton height={20} /></td>
                      <td className="py-3 px-4"><Skeleton height={20} /></td>
                      <td className="py-3 px-4"><Skeleton height={20} /></td>
                      <td className="py-3 px-4"><Skeleton height={20} /></td>
                    </tr>
                  ))
                ) : (
                  last10Users.map((user, index) => (
                    <tr key={index} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                      <td className="py-3 px-4 font-mono">
                        <Link to={`/pluto/user/${user.id}`} className="text-blue-400 hover:text-blue-300 transition-colors">
                          {user.id}
                        </Link>
                      </td>
                      <td className="py-3 px-4 font-mono text-green-400">${numberWithCommas(user.balance)}</td>
                      <td className="py-3 px-4 text-gray-300">{user.name}</td>
                      <td className="py-3 px-4 text-gray-300">{user.email_provider}</td>
                      <td className="text-right py-3 px-4 text-gray-300">{user.country}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlutoMain